import { useLocation, useRouter } from "@tanstack/react-router";
import { createContext, useContext, useMemo } from "react";

/*
  Color Scheme by 4Ps
  Product Content: #301B53
  Placement: #E079A7
  Pricing: #D9336C
  Promotion: #852B68
*/

const DEFAULT_SIDEBAR_COLOR = "#2e0054";
export const PLACEMENT_SIDEBAR_COLOR = "#E079A7";
const PRODUCT_CONTENT_SIDEBAR_COLOR = "#301B53";
const PRICING_SIDEBAR_COLOR = "#D9336C";
const PROMOTIONS_SIDEBAR_COLOR = "#852B68";

export const SIDEBAR_COLORS = {
  advertising: PLACEMENT_SIDEBAR_COLOR,
  merchandising: DEFAULT_SIDEBAR_COLOR,
  pricing: PRICING_SIDEBAR_COLOR,
  growth: DEFAULT_SIDEBAR_COLOR,
  admin: DEFAULT_SIDEBAR_COLOR,
  reports: DEFAULT_SIDEBAR_COLOR,
  placement: PLACEMENT_SIDEBAR_COLOR,
  promotions: PROMOTIONS_SIDEBAR_COLOR,
  main: DEFAULT_SIDEBAR_COLOR,
  product: PRODUCT_CONTENT_SIDEBAR_COLOR,
  staff: DEFAULT_SIDEBAR_COLOR,
};
export type SIDEBAR_SECTION = keyof typeof SIDEBAR_COLORS;

const DashboardThemeContext = createContext<{ sidebarColor: string; section: SIDEBAR_SECTION }>({
  sidebarColor: DEFAULT_SIDEBAR_COLOR,
  section: "main",
});

/** `/user`-relative paths of the routes that declare an application, keyed by that application. */
const useRoutePathsByApplication = () => {
  const router = useRouter();
  return useMemo(() => {
    const routeMap: Record<string, string[]> = {};
    for (const route of Object.values(router.routesById)) {
      const application = route.options.staticData?.application;
      if (!application) continue;
      const path = route.fullPath.replace(/^\/user/, "").replace(/(.)\/$/, "$1");
      const paths = routeMap[application];
      if (paths) {
        paths.push(path);
      } else {
        routeMap[application] = [path];
      }
    }
    return routeMap;
  }, [router]);
};

const getDashboardSectionByRoute = (routeMap: Record<string, string[]>, pathname: string): SIDEBAR_SECTION => {
  if (pathname.includes("user")) {
    pathname = pathname.split("/user")[1] ?? "";
  }

  if (routeMap["Promotions"]?.includes(pathname) || pathname.includes("promotions")) {
    return "promotions";
  }

  if (routeMap["Pricing"]?.includes(pathname) || pathname.includes("/pricing")) {
    return "pricing";
  }

  if (routeMap["Product"]?.includes(pathname)) {
    return "product";
  }

  if (
    routeMap["Placement"]?.includes(pathname) ||
    routeMap["Advertising"]?.includes(pathname) ||
    pathname.includes("advertising") ||
    pathname.includes("amc")
  ) {
    return "placement";
  }

  if (routeMap["Reports"]?.includes(pathname)) {
    return "reports";
  }

  if (routeMap["Staff"]?.includes(pathname)) {
    return "staff";
  }

  if (routeMap["Merchandising"]?.includes(pathname)) {
    return "merchandising";
  }

  if (routeMap["Admin"]?.includes(pathname)) {
    return "admin";
  }

  return "main";
};

export const DashboardThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const pathname = useLocation({ select: (location) => location.pathname });
  const routeMap = useRoutePathsByApplication();
  const currentDashboardSection = getDashboardSectionByRoute(routeMap, pathname);

  const dashboardTheme = useMemo(
    () => ({
      sidebarColor: SIDEBAR_COLORS[currentDashboardSection],
      section: currentDashboardSection,
    }),
    [currentDashboardSection],
  );

  return <DashboardThemeContext.Provider value={dashboardTheme}>{children}</DashboardThemeContext.Provider>;
};

const useDashboardTheme = () => useContext(DashboardThemeContext);

export default useDashboardTheme;
